import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ProducerResponse,
  CreateProducerRequest,
  CreateProducerResponse,
  RoleAssignmentResponse,
  ProducerRole,
  ProducerDropdownOption,
  ProducerDropdownResponse,
  ALL_PRODUCERS_ID
} from '../models/producer.model';

export interface ResendActivationEmailResponse {
  producerId: number;
  producerName: string;
  email: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProducerService {
  private readonly apiUrl = `${environment.apiUrl}/api/v1/producers`;

  constructor(private http: HttpClient) {}

  /**
   * Get list of all producers (admin only)
   */
  getProducers(): Observable<ProducerResponse[]> {
    return this.http.get<ProducerResponse[]>(this.apiUrl);
  }

  /**
   * Create a new producer (admin only)
   */
  createProducer(request: CreateProducerRequest): Observable<CreateProducerResponse> {
    return this.http.post<CreateProducerResponse>(this.apiUrl, request);
  }

  /**
   * Get current user's producer information
   */
  getCurrentProducer(): Observable<ProducerResponse> {
    return this.http.get<ProducerResponse>(`${this.apiUrl}/me`);
  }

  /**
   * Get roles for a specific producer
   */
  getProducerRoles(producerId: number): Observable<RoleAssignmentResponse[]> {
    return this.http.get<RoleAssignmentResponse[]>(`${this.apiUrl}/${producerId}/roles`);
  }

  /**
   * Assign a role to a producer (admin only)
   */
  assignRole(producerId: number, role: ProducerRole): Observable<RoleAssignmentResponse> {
    return this.http.post<RoleAssignmentResponse>(`${this.apiUrl}/${producerId}/roles`, { role });
  }

  /**
   * Remove a role from a producer (admin only)
   */
  removeRole(producerId: number, roleId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${producerId}/roles/${roleId}`);
  }

  /**
   * Resend activation email to a producer (admin only)
   */
  resendActivationEmail(producerId: number): Observable<ResendActivationEmailResponse> {
    return this.http.post<ResendActivationEmailResponse>(`${this.apiUrl}/${producerId}/resend-activation`, {});
  }

  /**
   * Get display name for a producer
   */
  getProducerDisplayName(producer: ProducerResponse): string {
    if (producer.firstName && producer.lastName) {
      return `${producer.firstName} ${producer.lastName}`;
    }
    return producer.firstName || producer.lastName || producer.email || 'Unknown';
  }

  /**
   * Get producers for dropdown (admin only)
   */
  getProducersForDropdown(): Observable<ProducerDropdownOption[]> {
    return this.http.get<ProducerDropdownResponse[]>(`${environment.apiUrl}/api/v1/quotes/producers`).pipe(
      map(producers => [
        { id: ALL_PRODUCERS_ID, displayName: 'All Producers' },
        ...producers.map(p => ({ id: p.id, displayName: p.displayName }))
      ])
    );
  }
}

